import * as React from 'react';
import { createShortSlug } from '@bondvet/types/website/util';
import { navigate } from 'gatsby';
import { useLocation } from '@reach/router';
import {
    getCityId,
    getClosestAllowedStep,
    getLocationId,
    getSteps,
    hasBookingBooted,
} from 'booking/redux/selectors';
import { useSelector } from 'react-redux';
import { BookingStep } from 'booking/lib/types';
import { BOOKING_URL } from 'core/constants';
import { getBookingStepUrl } from '../lib/utils';
import useBookingContext from './useBookingContext';

type UseStep = {
    next: (overrideCitySlug?: string, overrideLocationSlug?: string) => void;
    back: () => void;
    step: BookingStep;
    stepAllowed: boolean;
};

function kebabToCamel(kebabString: string): string {
    return kebabString.replace(/-([a-z])/g, (match, letter) =>
        letter.toUpperCase(),
    );
}

export default function useStep(checkPath = false): UseStep {
    const steps = useSelector(getSteps);
    const booted = useSelector(hasBookingBooted);
    const cityId = useSelector(getCityId);
    const locationId = useSelector(getLocationId);
    const location = useLocation();
    const params = new URLSearchParams(location?.search);
    const sessionId = params.get('bookingSessionId');

    const { citySlug, locationSlug } = useBookingContext();

    const step = React.useMemo(() => {
        const bookingPathPart = (location?.pathname ?? '').replace(
            `/${BOOKING_URL}/`,
            '',
        );
        const match = bookingPathPart.match(
            /^(?<city>[^/]+)(?:\/(?<location>[^/]+)(?:\/(?<step>[^/]+))?)?/,
        );

        switch (true) {
            case bookingPathPart === 'city':
                return 'city';
            case !!match && !!match.groups?.step:
                return kebabToCamel(match.groups.step) as BookingStep;
            case !!match && !!match.groups?.location:
                return 'date';
            case !!match && !match.groups?.location:
                return 'location';
            default:
                return steps[0];
        }
    }, [steps, location?.pathname]);

    const closestAllowedStep = useSelector(getClosestAllowedStep(step));

    React.useEffect(() => {
        if (
            checkPath &&
            booted &&
            step &&
            closestAllowedStep !== step &&
            !sessionId
        ) {
            console.warn(
                `step ${step} not allowed. redirecting to ${closestAllowedStep}`,
                new Error('').stack,
            );

            if (citySlug) {
                let path = `/${BOOKING_URL}/${citySlug}`;

                if (locationSlug) {
                    path = `${path}/${locationSlug}`;
                }

                if (location?.pathname !== path) {
                    navigate(path, { replace: true });
                }

                return;
            }

            navigate(`/${BOOKING_URL}/${closestAllowedStep}`, {
                replace: true,
            });
        }
    }, [
        checkPath,
        closestAllowedStep,
        step,
        booted,
        sessionId,
        citySlug,
        locationSlug,
        location?.pathname,
    ]);

    const cityIdRef = React.useRef<string | null>(null);
    const locationIdRef = React.useRef<string | null>(null);

    React.useEffect(() => {
        cityIdRef.current = cityId;
        locationIdRef.current = locationId;
    }, [cityId, locationId]);

    const moveSteps = React.useCallback(
        (
            stepsToMove: number,
            overrideCitySlug?: string,
            overrideLocationSlug?: string,
        ) => {
            if (stepsToMove === 0) {
                return;
            }

            const currentIndex = steps.findIndex((name) => name === step);
            const targetIndex = currentIndex + stepsToMove;

            const citySlugToUse = overrideCitySlug || citySlug;
            let locationSlugToUse = overrideLocationSlug || locationSlug;

            // make sure we definitely use the short location slug (i.e. without "-animal-hospital")
            if (locationSlugToUse) {
                locationSlugToUse = createShortSlug(locationSlugToUse);
            }

            const fallback = (
                citySlugToFallBackTo?: string,
                locationSlugToFallBackTo?: string,
            ) => {
                if (citySlugToFallBackTo) {
                    if (locationSlugToFallBackTo) {
                        navigate(
                            `/${BOOKING_URL}/${citySlugToFallBackTo}/${locationSlugToFallBackTo}`,
                        );
                        return;
                    }

                    navigate(`/${BOOKING_URL}/${citySlugToFallBackTo}`);
                    return;
                }

                navigate(`/${BOOKING_URL}/city`);
            };

            if (targetIndex > -1 && targetIndex < steps.length) {
                const nextStep = steps[targetIndex];
                switch (nextStep) {
                    case 'city':
                        navigate(`/${BOOKING_URL}/city`);
                        break;
                    case 'location': {
                        fallback(citySlugToUse);
                        break;
                    }
                    case 'date': {
                        fallback(citySlugToUse, locationSlugToUse);
                        break;
                    }
                    default:
                        if (citySlugToUse && locationSlugToUse) {
                            navigate(
                                getBookingStepUrl(
                                    nextStep,
                                    citySlugToUse,
                                    locationSlugToUse,
                                ),
                            );
                        } else {
                            fallback(citySlugToUse);
                        }
                        break;
                }
            }
        },
        [steps, step, citySlug, locationSlug],
    );

    const back = React.useCallback(() => {
        moveSteps(-1);
    }, [moveSteps]);

    const next = React.useCallback(
        (overrideCitySlug?: string, overrideLocationSlug?: string) => {
            moveSteps(1, overrideCitySlug, overrideLocationSlug);
        },
        [moveSteps],
    );

    return { step, next, back, stepAllowed: step === closestAllowedStep };
}
